export type RectTuple = [number, number, number, number];
export type CircleTuple = [number, number, number];

export type ElectionDetails = {
  year: number;
  legalName: string;
  state: string;
  partyName: string;
  campaignCnpj: string;
  dataProtectionContact: string;
  dataProtectionOfficer: string;
};

export type CastAnchor = {
  /** Centro horizontal entre os olhos, em fração da largura do recorte. */
  eyeX: number;
  /** Linha dos olhos, em fração da altura do recorte. */
  eyeY: number;
  /** Distância interocular, em fração da largura do recorte. */
  interocular: number;
};

export type CastMember = {
  id: string;
  name: string;
  number: string;
  office: string;
  party: string;
  cutout: string;
  anchor: CastAnchor;
};

export type StagePlacement = {
  /** Altura da linha dos olhos no canvas, em pixels. */
  eyeLineY: number;
  /** Distância interocular alvo no canvas, em pixels. Normaliza a escala dos rostos. */
  interocular: number;
  /** Distância horizontal entre os centros de rosto, em pixels. */
  spacing: number;
};

export type StageSpec = {
  /** Altura em que as figuras terminam. */
  cutLine: number;
  /** Altura da dissolução acima da cutLine. */
  fade: number;
  /** Base do selo de montagem digital. Sem este campo, nenhum selo é desenhado. */
  sealY?: number;
  /** Recorta o palco pelo clipCircle do layout. */
  clip?: boolean;
  byCount: Record<string, StagePlacement>;
};

/** Proporção do card na grade. Vem medida do arquivo por `npm run gallery:prepare`. */
export type GalleryFormat = "story" | "portrait" | "feed" | "wide";

export type GalleryItem = {
  id: string;
  label: string;
  format: GalleryFormat;
  /**
   * Medidas reais do arquivo. O card usa a proporção medida, não a canônica do formato:
   * arte de impressão sai em 1508×1080, e desenhá-la em 16/9 cortaria a borda — que é
   * justamente onde mora o número de urna.
   */
  width: number;
  height: number;
  /** Arte cheia, em tamanho de publicação. É ela que a pessoa baixa. Imagem ou .mp4. */
  file: string;
  /** Segundos, só em vídeo. Vai no selo do card: 16 MB sem aviso é surpresa em rua. */
  duration?: number;
  /** Miniatura da grade. Sem ela a grade baixaria as artes cheias só para desenhar cards. */
  thumb?: string;
  /** Texto de compartilhamento próprio. Sem ele vale o `shareText` da campanha. */
  shareText?: string;
};

export type Gallery = {
  title: string;
  note: string;
  items: GalleryItem[];
};

export type Campaign = {
  slug: string;
  status: "draft" | "ready";
  assetsVerified: boolean;
  candidate: string;
  shortName: string;
  number: string;
  office: string;
  party: string;
  slogan: string;
  supportingText: string;
  shareText: string;
  election?: ElectionDetails;
  assets: {
    portrait: string;
    storyFrame: string;
    avatarFrame: string;
    castFrame?: string;
  };
  frameSources?: Partial<Record<"story" | "avatar", string>>;
  theme: {
    deep: string;
    surface: string;
    surfaceRaised: string;
    action: string;
    accent: string;
    success: string;
    ink: string;
    muted: string;
  };
  layouts: {
    story: { width: number; height: number; photoRect: RectTuple };
    avatar: { width: number; height: number; photoRect: RectTuple; clipCircle: CircleTuple; clipPhoto?: boolean };
    cast?: { width: number; height: number; photoRect: RectTuple; clipCircle?: CircleTuple };
  };
  cast?: { primary: CastMember; partners: CastMember[] };
  stage?: StageSpec;
  gallery?: Gallery;
  seo: { title: string; description: string };
  deploy: { host: string; stack: string; image: string; router: string; replicas: number };
};

declare const __CAMPAIGN__: Campaign;

export const campaign = __CAMPAIGN__;
export const campaignAsset = (relativePath: string) => `/campaign/${relativePath}`;
